import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardList, CheckCircle2, AlertCircle, Building2, FileText, Wallet as WalletIcon, Sheet, IndianRupee } from "lucide-react";
import DashboardLayout, { KpiCard } from "@/components/DashboardLayout";
import { api, formatError } from "@/lib/api";
import { StatusBadge } from "@/components/Brand";
import { MakePaymentBody } from "@/components/MakePayment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/vendor", label: "Dashboard", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/vendor/team", label: "Team / टीम", key: "team", icon: Users },
  { to: "/vendor/leads", label: "Leads", key: "leads", icon: ClipboardList },
  { to: "/vendor/wallet", label: "Wallet / वॉलेट", key: "wallet", icon: WalletIcon },
  { to: "/vendor/make-payment", label: "Make Payment", key: "make-payment", icon: IndianRupee },
  { to: "/vendor/commission", label: "Commission Sheet", key: "commission", icon: Sheet },
  { to: "/vendor/reports", label: "Reports", key: "reports", icon: FileText },
];

function Home() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/reports/summary").then((r) => setS(r.data)); }, []);
  return (
    <DashboardLayout roleLabel="Vendor Panel" nav={nav} title="Vendor Dashboard" subtitle="Team और leads का overview">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="v-total-leads" label="Total Leads" value={s?.total_leads ?? "—"} icon={ClipboardList} accent="blue" />
        <KpiCard testId="v-approved" label="Approved" value={s?.approved ?? "—"} accent="green" icon={CheckCircle2} />
        <KpiCard testId="v-pending" label="Pending" value={s?.pending ?? "—"} accent="orange" icon={AlertCircle} />
        <KpiCard testId="v-merchants" label="My Merchants" value={s?.total_merchants ?? "—"} accent="violet" icon={Building2} />
      </div>
      <div className="mt-4 bg-white border rounded-md p-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Commission</div>
        <p className="text-sm text-slate-600 mt-2">Commission sheet monthly आप को email पर भेजी जाएगी. किसी भी query के लिए admin से contact करें.</p>
      </div>
    </DashboardLayout>
  );
}

function TeamPage() {
  const [rows, setRows] = useState([]); const [role, setRole] = useState("employee");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ mobile: "", password: "", name: "", email: "", role: "employee", business_name: "" });
  const load = () => api.get("/users", { params: { role } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [role]);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/users", { ...form, role }); toast.success("Created"); setOpen(false); setForm({ mobile: "", password: "", name: "", email: "", role: "employee", business_name: "" }); load(); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <DashboardLayout roleLabel="Vendor Panel" nav={nav} title="Team / टीम" subtitle="अपने employees और merchants manage करें">
      <div className="flex items-center justify-between mb-4">
        <div className="w-48"><Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="v-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="employee">Employees</SelectItem>
            <SelectItem value="merchant">Merchants</SelectItem>
          </SelectContent>
        </Select></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1a3ba8] hover:bg-[#17348f] text-white" data-testid="v-add-btn">+ Add {role === "employee" ? "Employee" : "Merchant"}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create {role}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="v-new-name" /></div>
              <div><Label>Mobile</Label><Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="v-new-mobile" /></div>
              <div><Label>Password</Label><Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="v-new-pwd" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="v-new-email" /></div>
              <div className="col-span-2"><Label>Business (optional)</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="v-submit">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Business</TableHead><TableHead>KYC</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id}><TableCell className="font-medium">{u.name}</TableCell><TableCell className="font-mono text-sm">{u.mobile}</TableCell><TableCell>{u.business_name || "—"}</TableCell><TableCell>{u.kyc_status ? <StatusBadge status={u.kyc_status} /> : "—"}</TableCell></TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No members yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function LeadsPage() {
  const [leads, setLeads] = useState([]); const [svcs, setSvcs] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)); api.get("/services").then((r) => setSvcs(r.data)); }, []);
  return (
    <DashboardLayout roleLabel="Vendor Panel" nav={nav} title="Leads" subtitle="Team के सभी leads">
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Service</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {leads.map((l) => { const s = svcs.find((x) => x.id === l.service_id); return (
              <TableRow key={l.id}><TableCell>{l.customer_name}</TableCell><TableCell className="font-mono text-sm">{l.customer_mobile}</TableCell><TableCell>{s?.name || l.service_id}</TableCell><TableCell><StatusBadge status={l.status} /></TableCell><TableCell className="text-xs text-slate-500">{(l.created_at || "").split("T")[0]}</TableCell></TableRow>
            ); })}
            {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No leads</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

export default function VendorDashboard() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="team" element={<TeamPage />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="wallet" element={<VendorWalletPage />} />
      <Route path="make-payment" element={<VendorMakePaymentPage />} />
      <Route path="commission" element={<VendorCommissionPage />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="*" element={<Navigate to="/vendor" replace />} />
    </Routes>
  );
}

function VendorMakePaymentPage() {
  return (
    <DashboardLayout roleLabel="Vendor Panel" nav={nav} title="Make Payment / भुगतान करें" subtitle="Admin के active QR पर pay करें और details submit करें">
      <MakePaymentBody />
    </DashboardLayout>
  );
}

function VendorWalletPage() {
  const { user, refreshMe } = useAuth();
  const [txs, setTxs] = useState([]); const [wr, setWr] = useState([]);
  const [wForm, setWForm] = useState({ amount: "", method: "bank", account_details: "" });
  const load = async () => { setTxs((await api.get("/wallet/tx")).data); setWr((await api.get("/withdrawals")).data); await refreshMe(); };
  useEffect(() => { load(); }, []);
  const req = async (e) => { e.preventDefault(); try { await api.post("/withdrawals", { ...wForm, amount: Number(wForm.amount) }); toast.success("Withdrawal requested"); setWForm({ amount: "", method: "bank", account_details: "" }); load(); } catch (err) { toast.error(formatError(err)); } };
  return (
    <DashboardLayout roleLabel="Vendor Panel" nav={nav} title="Wallet / वॉलेट" subtitle="Admin से मिली commission और transactions">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard testId="v-wal-bal" label="Available" value={`₹ ${Number(user?.wallet_balance || 0).toFixed(2)}`} accent="green" icon={WalletIcon} />
        <KpiCard testId="v-wal-lock" label="Locked" value={`₹ ${Number(user?.wallet_locked || 0).toFixed(2)}`} accent="orange" />
        <div className="stat-card bg-white border rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Withdrawal Request / निकासी</div>
          <form onSubmit={req} className="mt-3 space-y-2">
            <Input placeholder="Amount" type="number" required value={wForm.amount} onChange={(e) => setWForm({ ...wForm, amount: e.target.value })} data-testid="v-with-amt" />
            <Input placeholder="Bank a/c / UPI details" value={wForm.account_details} onChange={(e) => setWForm({ ...wForm, account_details: e.target.value })} data-testid="v-with-acc" />
            <Button type="submit" className="w-full bg-orange-gradient text-white rounded-full" data-testid="v-with-submit">Request Withdrawal</Button>
          </form>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-2">Wallet Transactions</div>
        <div className="bg-white border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Note</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
            <TableBody>
              {txs.map((t) => (<TableRow key={t.id}><TableCell className="uppercase text-xs">{t.type}</TableCell><TableCell>₹ {Number(t.amount).toFixed(2)}</TableCell><TableCell>{t.note || "—"}</TableCell><TableCell className="text-xs text-slate-500">{(t.created_at || "").replace("T"," ").slice(0,16)}</TableCell></TableRow>))}
              {txs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No transactions</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-2">Withdrawal Requests</div>
        <div className="bg-white border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
            <TableBody>
              {wr.map((w) => (<TableRow key={w.id}><TableCell>₹ {Number(w.amount).toFixed(2)}</TableCell><TableCell>{w.method}</TableCell><TableCell><StatusBadge status={w.status === "pending" ? "pending" : w.status === "approved" ? "approved" : "rejected"} /></TableCell><TableCell className="text-xs text-slate-500">{(w.created_at || "").split("T")[0]}</TableCell></TableRow>))}
              {wr.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No requests</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function VendorCommissionPage() {
  const [items, setItems] = useState([]); const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setItems(r.data.filter((s) => s.active !== false))); api.get("/categories").then((r) => setCats(r.data)); }, []);
  return (
    <DashboardLayout roleLabel="Vendor Panel" nav={nav} title="Commission Sheet / कमीशन शीट" subtitle="हर service पर आपको (Vendor) और Merchant को कितना commission मिलेगा">
      <div className="bg-white border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>SR</TableHead><TableHead>Service Name</TableHead><TableHead>Category</TableHead>
            <TableHead>Type</TableHead><TableHead>Amount</TableHead>
            <TableHead>Merchant ₹</TableHead><TableHead>Vendor ₹ (आपको)</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s, i) => { const cat = cats.find((c) => c.id === s.category_id); return (
              <TableRow key={s.id} data-testid={`v-comm-row-${s.id}`}>
                <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{cat?.name || "—"}</TableCell>
                <TableCell>
                  {s.charge_type === "paid"
                    ? <span className="chip border bg-orange-50 text-orange-700 border-orange-200">CHARGES</span>
                    : <span className="chip border bg-emerald-50 text-emerald-700 border-emerald-200">FREE</span>}
                </TableCell>
                <TableCell>{s.charge_type === "paid" ? `₹ ${Number(s.charge_amount || 0).toFixed(2)}` : "—"}</TableCell>
                <TableCell>₹ {Number(s.merchant_commission || 0).toFixed(2)}</TableCell>
                <TableCell className="font-medium text-[#1a3ba8]">₹ {Number(s.vendor_commission || 0).toFixed(2)}</TableCell>
              </TableRow>
            ); })}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">कोई active service नहीं मिली</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function ReportsPage() {
  const dl = (kind, fmt) => {
    const token = localStorage.getItem("epc_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${fmt}?kind=${kind}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${kind}.${fmt === "xlsx" ? "xlsx" : "pdf"}`; a.click(); });
  };
  const reports = [
    { kind: "leads", name: "My Team's Leads", desc: "आपके team के सभी leads (status सहित)", grad: "bg-brand-gradient" },
    { kind: "employees", name: "Employees", desc: "आपके सभी employees की list", grad: "bg-violet-gradient" },
    { kind: "merchants", name: "Merchants", desc: "आपके merchants की list — KYC & wallet", grad: "bg-emerald-gradient" },
  ];
  return (
    <DashboardLayout roleLabel="Vendor Panel" nav={nav} title="Reports / रिपोर्ट्स" subtitle="Excel और PDF दोनों में download करें">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map((r) => (
          <div key={r.kind} className="stat-card bg-white border rounded-xl p-6 relative overflow-hidden">
            <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full ${r.grad} opacity-10`} />
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{r.name}</div>
            <h3 className="font-display text-xl font-semibold mt-2">{r.name}</h3>
            <p className="text-sm text-slate-500 mt-2">{r.desc}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" className="bg-emerald-gradient hover:opacity-90 text-white" onClick={() => dl(r.kind, "xlsx")} data-testid={`v-dl-xlsx-${r.kind}`}>Excel</Button>
              <Button size="sm" className="bg-rose-gradient hover:opacity-90 text-white" onClick={() => dl(r.kind, "pdf")} data-testid={`v-dl-pdf-${r.kind}`}>PDF</Button>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
